import cx from 'classnames'
import { compact, filter } from 'lodash'
import * as React from 'react'
import {
  matchPath,
  NavLink,
  useLocation,
  useRouteMatch
} from 'react-router-dom'
import Tooltip from '@src/components/common/tooltip'
import { Routes } from '@src/constants/routes'
import { CSSSelector } from '@src/utils/css.selector.helper'
import {
  generateUrl,
  isPageByPathNameFromLocation
} from '@src/utils/router.helpers'
import type { SettingsPageParams } from '../settings'
import { Logo } from '@src/components/logo'
import { routes } from '../shell/configurations/routes'
import { MenuItem, MenuSection } from './components'
import {
  MenuItemData,
  MenuItemDataWithSubItems,
  MenuItems,
  selectorSources
} from './configurations'
import * as styles from './sidebar.layout.styl'
import * as Selectors from './selectors'
import * as CommonSelectors from '@src/selectors'
import { useDispatch, useSelector } from 'react-redux'
import { getAlertsCount } from '@src/redux/actions/dashboard.actions'
import {
  getResourcesAccessSet,
  getSourcesAccessSet,
  getAlertsuardrailsComplianceAccessSet,
  useFeatureFlagsContext
} from '../feature.flags/context'
import { getUseChangesPage } from '@src/selectors'
import { dashboardMenuItem } from '@src/endpoints/feature.flag'

export type OwnProps = {
  collapsed: boolean
  refresh: () => void
}

export type Props = OwnProps

type SidebarComponent<T> = React.FC<T> & {
  CSSSelectors: {
    logoSelector?: CSSSelector
  }
}

const resourcesAccessSet = getResourcesAccessSet()
const alertsAccessSet = getAlertsuardrailsComplianceAccessSet()
const sourcesAccessSet = new Set([
  ...getSourcesAccessSet(),
  ...getResourcesAccessSet()
])

const Sidebar: SidebarComponent<Props> = (props: Props) => {
  const { refresh, collapsed } = props
  const match = useRouteMatch<{}>()
  const location = useLocation()

  const dispatch = useDispatch()

  const tenant = useSelector(CommonSelectors.getTenant)
  const useChangesPage = useSelector(getUseChangesPage)
  const alertCounter = useSelector(Selectors.getAlertCounter)
  const {
    CAM_TEMP_20210127_USE_REPORT_SERVICE,
    CAM_TEMP_20210817_ALERTS_IMPROVEMENTS,
    CAM_TEMP_20220209_SOURCES_IMPROVEMENTS,
    CAM_PERM_20220804_HIDE_DASHBOARD,
    CAM_PERM_20220804_HIDE_RESOURCES_ACCESS,
    CAM_PERM_20220804_HIDE_ALERTS_GUARDRAILS,
    CAM_PERM_20220804_HIDE_ALL_SOURCE
  } = useFeatureFlagsContext()
  const showReportMenu = CAM_TEMP_20210127_USE_REPORT_SERVICE?.response?.value
  const sourceImprovements =
    CAM_TEMP_20220209_SOURCES_IMPROVEMENTS?.response?.value
  const alertsImprovementsFlag =
    CAM_TEMP_20210817_ALERTS_IMPROVEMENTS?.response?.value
  const alertsPending =
    CAM_TEMP_20210817_ALERTS_IMPROVEMENTS?.pending ||
    CAM_PERM_20220804_HIDE_ALL_SOURCE?.response?.value
  const hideDashboard = CAM_PERM_20220804_HIDE_DASHBOARD?.response?.value
  const resourcesAccessHide =
    CAM_PERM_20220804_HIDE_RESOURCES_ACCESS?.response?.value ||
    CAM_PERM_20220804_HIDE_ALL_SOURCE?.response?.value
  const hideAllSources = CAM_PERM_20220804_HIDE_ALL_SOURCE?.response?.value
  const alertsAccessHide =
    CAM_PERM_20220804_HIDE_ALERTS_GUARDRAILS?.response?.value ||
    CAM_PERM_20220804_HIDE_ALL_SOURCE?.response?.value
  function fetchCounters(callback?: () => void) {
    const isDashboardPage = !!matchPath(
      location.pathname,
      Routes?.pages?.dashboard
    )
    if (!alertsAccessHide && !isDashboardPage && !alertsPending) {
      dispatch(getAlertsCount({ callback, useV2: alertsImprovementsFlag }))
    }
  }

  function renderButtons(isCollapsed = false) {
    const { account_info = [] } = tenant || {}
    const hasAccounts = account_info && account_info.length > 0
    const notExistTenant = !tenant || !hasAccounts
    const isActive = isPageByPathNameFromLocation('/settings', location)
    const isDisabled =
      notExistTenant &&
      isPageByPathNameFromLocation(Routes?.pages?.setup?.path, location)
    const tooltipPlacement = isCollapsed ? 'bottom' : 'top'

    return (
      <div role={'sidebar_menu_buttons'} className={styles.buttons}>
        <Tooltip description={'Refresh'} placement={tooltipPlacement}>
          <i onClick={onRefresh} className="material-icons">
            refresh
          </i>
        </Tooltip>
        <Tooltip description={'Settings'} placement={tooltipPlacement}>
          <NavLink
            id="Settings"
            to={generateUrl<SettingsPageParams>(Routes?.pages?.settings?.path, {
              id: 'alertshigh'
            })}
            className={cx({
              [styles.active]: isActive,
              isDisabled
            })}
          >
            <i className="material-icons">settings</i>
          </NavLink>
        </Tooltip>
      </div>
    )
  }

  function onRefresh() {
    refresh()
    fetchCounters()
  }

  const renderCollapsed = () => (
    <div className={styles.item}>
      <span className={styles.iconTools} />
      {renderButtons(true)}
    </div>
  )

  React.useEffect(() => {
    fetchCounters()
  }, [])

  function renderMenuItems(items: (MenuItemData | MenuItemDataWithSubItems)[]) {
    if (!sourceImprovements) {
      const subItems = MenuItems[2][0]['subItems']
      const doesNotHaveSnapshotsOption = !subItems.some(
        item => item.title === 'Snapshots'
      )
      if (doesNotHaveSnapshotsOption) {
        subItems.push({
          title: 'Snapshots',
          url: 'sources/snapshot',
          path: [`${routes?.accounts?.path}/snapshot/:page`],
          selector: selectorSources
        })
      }
    } else {
      const sourcesMenu = items.find(
        item => item.title === 'Sources'
      ) as MenuItemDataWithSubItems
      if (sourcesMenu) {
        sourcesMenu.subItems = filter(
          sourcesMenu.subItems,
          item => item.title !== 'Snapshots'
        )
      }
    }

    return compact(
      items.map((item, index) => {
        const commonProps = {
          isCollapsed: collapsed,
          selector:
            item?.selector ||
            item?.url ||
            (item.title || '').toLocaleLowerCase()
        }

        if (item?.url && item?.url === 'changes' && useChangesPage) {
          return (
            <MenuItem key={index} {...commonProps} {...item}>
              <i className="material-icons">sync</i>
            </MenuItem>
          )
        } else if (item?.url && item?.url === 'alerts') {
          return (
            <MenuItem
              key={index}
              {...commonProps}
              {...item}
              counter={alertCounter}
            >
              <i className="material-icons">notifications</i>
            </MenuItem>
          )
        }
        if (item.url === 'reports' && !showReportMenu) return null
        if (
          item.url === 'changes' &&
          useChangesPage !== null &&
          useChangesPage === false
        )
          return null
        return <MenuItem key={index} {...commonProps} {...item} />
      })
    )
  }

  const menuItemsDisabled =
    !tenant || !tenant.account_info || tenant.account_info.length === 0

  return (
    <div
      role={'sidebar_menu'}
      className={cx(styles.sidebar, {
        [styles.collapsed]: collapsed
      })}
    >
      <Logo
        size={collapsed ? 'small' : 'full'}
        role={Sidebar.CSSSelectors.logoSelector.value}
      />

      {/* <div className={styles.logoWrapper}>
        <div
          role={Sidebar.CSSSelectors.logoSelector.value}
          className={styles.logo}
        />
      </div> */}
      <div className={styles.separator} />
      {collapsed ? renderCollapsed() : renderButtons()}
      {MenuItems.map(menuItem => {
        if (resourcesAccessHide) {
          return menuItem.filter(item => !resourcesAccessSet.has(item.selector))
        }
        return menuItem
      })
        .map(menuItem => {
          if (alertsAccessHide) {
            return menuItem.filter(item => !alertsAccessSet.has(item.selector))
          }
          return menuItem
        })
        .map(menuItem => {
          if (hideAllSources) {
            return menuItem.filter(item => !sourcesAccessSet.has(item.selector))
          }
          return menuItem
        })
        .map(menuItem => {
          if (hideDashboard) {
            return menuItem.filter(item => item.title !== dashboardMenuItem)
          }
          return menuItem
        })
        .map((section, index) => (
          <React.Fragment key={index}>
            <div className={styles.separator} />
            <MenuSection
              isDisabled={
                menuItemsDisabled && section[0]?.selector !== selectorSources
              }
              match={match}
            >
              {renderMenuItems(section)}
            </MenuSection>
          </React.Fragment>
        ))}
    </div>
  )
}

Sidebar.CSSSelectors = {
  logoSelector: new CSSSelector('logoApp', 'div')
}

export default Sidebar
